<template>
	<div class="menu-settings">
		<CardSlashedMenuLink />

		<CardSlashed v-if="showInfoBanner" class="card-info-banner">
			<template #left>
				<div class="card-info-banner-title">
					<h2>{{ getString('card_info_title_menu') }}</h2>
					<p>{{ getString('card_info_description_1_menu') }}</p>
					<p class="br">{{ getString('card_info_description_2_menu') }}</p>
				</div>
			</template>
			<template #right>
				<div class="wrapper-img">
					<picture>
						<source type="image/webp" srcset="https://img.qromo.io/img/image-info-banner-menu-digitale.webp">
						<img src="https://img.qromo.io/img/image-info-banner-menu-digitale.png" alt="product image">
					</picture>
				</div>
			</template>
		</CardSlashed>

		<div class="menu-edit-body">
			<div class="menu-editor-container">
				<AppCard :title="getString('menu_customization', [userStore.menuWord])" type="savable" :button-text="getString('save')" @button-click="saveMenuCustomization">
					<template #body>
						<AppList :items="this.items">
							<template #color-slot="{item}">
								<div class="row centered space-between color-slot">
									<p class="list-item-title">{{ item.title }}</p>
									<AppColorPicker :ref="item.ref" :value="item.color" @changed-color="item.callback" />
								</div>
							</template>
							<template #image-slot="{item}">
								<div class="row centered space-between image-slot">
									<p class="list-item-title">{{ item.title }}</p>
									<AppDropzone
										:max-bytes="80 * 1024"
										:max-width="300"
										:max-height="300"
										:files="item.image ? [item.image] : []"
										@removed-file="userStore.currBusiness.logo = null"
										@upload-complete="item.callback" />
								</div>
							</template>
							<template #text-slot="{item}">
								<div class="row centered space-between text-slot">
									<p class="list-item-title-40">{{ item.title }}</p>
									<AppTextInput :textarea="item.textarea" :value="item.value" :ref="item.ref" :placeholder="item.placeholder" @changed-text="item.callback" />
								</div>
							</template>
							<template #socials-slot="{item}">
								<div class="row centered">
									<SocialsList :ref="item.refSocials" />
								</div>
							</template>
							<template #select-theme-slot="{item}">
								<div class="row centered space-between theme-container">
									<p>{{ item.title }}</p>
									<ColorThemeChooser @themeSelected="refreshPreview" />
								</div>
							</template>
							<template #banner-slot="{item}">
								<BannerChooser @banner-chosen="(url) => { userStore.currBusiness ? userStore.currBusiness.picture = url : null }" />
							</template>
							<template #advanced-slot="{item}">
								<MenuAdvancedOptions @font-save-clicked="refreshPreview"/>
							</template>
						</AppList>
					</template>
				</AppCard>
				<AppCard class="card-margin" :title="getString('menu_features', [userStore.menuWord])" type="savable" :button-text="getString('save')" @button-click="saveMenuFeatures">
					<template #body>
						<AppList :items="this.itemsFeatures">
							<template #allergens-slot="{item}">
								<AllergensChooser />
							</template>
						</AppList>
					</template>
				</AppCard>
			</div>
			<div class="menu-preview-container">
				<MenuPreview ref="menu-preview" general-menu />
			</div>
		</div>
	</div>
</template>

<script>
import AppList from "@/shared/components/AppList.vue";
import AppCard from "@/shared/components/AppCard.vue";
import AppRadioButtonGroup from "@/shared/components/AppRadioButtonGroup.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import AppColorPicker from "@/shared/components/AppColorPicker.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppDropzone from "@/shared/components/AppDropzone.vue";
import SocialsList from "@/console-new/components/shared/SocialsList.vue";
import AllergensChooser from "@/console-new/components/config/settings/menu/AllergensChooser.vue";
import BannerChooser from "@/console-new/components/config/settings/menu/BannerChooser.vue";
import CardSlashedMenuLink from "@/console-new/components/shared/CardSlashedMenuLink.vue";
import CardSlashed from "@/console-new/components/shared/CardSlashed.vue";

import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useMenuStore, useSocialsStore, useUnchangingStore, useUserStore} from "@/console-new/stores";
import MenuAdvancedOptions from "@/console-new/components/config/settings/menu/MenuAdvancedOptions.vue";
import ColorThemeChooser from "@/console-new/components/config/settings/menu/ColorThemeChooser.vue";
import MenuPreview from "@/console-new/components/config/menu/MenuPreview.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		let menuStore = useMenuStore();
		const notificationStore = useNotificationStore();
		const unchangingStore = useUnchangingStore();
		const socialsStore = useSocialsStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, userStore, unchangingStore, socialsStore, menuStore}
	},
	name: "MenuSettingsContainer",
	components: {
		MenuPreview,
		CardSlashedMenuLink,
		MenuAdvancedOptions,
		AppDropdown,
		AppChevronIcon,
		AppTextInput,
		AppNumberInput,
		AppRadioButtonGroup,
		AppList,
		AppCard,
		AppColorPicker,
		AppSelect,
		AppDropzone,
		SocialsList,
		AllergensChooser,
		BannerChooser,
		ColorThemeChooser,
		CardSlashed
	},
	data() {
		return {
			extraCoverActive: false,
			allergensDropdownActive: false,
			bannerOpen: false,
			socialsOpen: false,
			advancedOpen: false,
		}
	},
	computed: {
		items() {
			return [
				{
					title: this.getString("business_name"),
					textInput: true,
					placeholder: this.getString("my_business"),
					value: this.userStore.currBusiness && this.userStore.currBusiness.name,
					callbackText: (value) => {
						this.userStore.currBusiness.name = value
					}
				},
				{title: this.getString("business_description"), textInput: true, placeholder: this.getString("business_description"), value: this.userStore.currBusiness && this.userStore.currBusiness.description, isTextArea: true, callbackText: (value) => {this.userStore.currBusiness.description = value}},
				{title: this.getString("business_color"), customSlot: "color-slot", color: this.userStore.currBusiness && this.userStore.currBusiness.color, callback: (value) => {this.userStore.currBusiness.color = value.replace("#", "")}},
				{title: this.getString("theme_color"), customSlot: "select-theme-slot", explain: this.getString("theme_color_explain")},
				{title: this.getString("business_logo"), customSlot: "image-slot", explain: this.getString("suggested_res_200"), image: this.userStore.currBusiness ? this.userStore.currBusiness.logo : null, callback: (files) => {this.userStore.currBusiness.logo = files[0]}},
				{title: this.getString("business_banner"), chevronItem: true, active: this.bannerOpen, explain: this.getString("suggested_res_1080_400"), dropdownSlot: "banner-slot", callback: (value) => {this.bannerOpen = value}},
				{
					title: this.getString("business_socials"),
					chevronItem: true,
					active: this.socialsOpen,
					dropdownSlot: 'socials-slot',
					refSocials: 'menu-socials',
					callback: (value) => {
						this.socialsOpen = value
					}
				},
				{title: this.getString("advanced_options"), chevronItem: true, active: this.advancedOpen, dropdownSlot: 'advanced-slot', callback: (value) => {this.advancedOpen = value}},
			];
		},
		itemsFeatures() {
			let features = [
				{
					title: this.getString("products_search"),
					explain: this.getString("products_search_explain", [this.userStore.menuWord]),
					switch: true,
					active: this.userStore.currBusiness && this.userStore.currBusiness.enabled_search,
					callback: (value) => {
						this.userStore.currBusiness.enabled_search = value
					}
				},
				{
					title: this.getString("read_mode"),
					explain: this.getString("read_mode_explain", [this.userStore.menuWord]),
					switch: true,
					active: this.userStore.currBusiness && this.userStore.currBusiness.read_mode,
					callback: (value) => {
						this.userStore.currBusiness.read_mode = value
					}
				}
			];
			if(this.userStore.currBusiness && this.userStore.currBusiness.type !== "other") {
				features.push({
					title: this.getString("allergens"),
					chevronItem: true,
					dropdownSlot: 'allergens-slot',
					switch: false,
					active: this.allergensDropdownActive,
					callback: (value) => {
						this.allergensDropdownActive = value
					}
				});
			}
			return features;
		},
		showInfoBanner() {
			let hasLink = this.userStore.currBusiness && (this.userStore.currBusiness.link != null && this.userStore.currBusiness.link.length > 0);
			let showingLinkBanner = this.userStore.currBusiness ? !hasLink : false;
			return !showingLinkBanner && this.userStore?.bannersData && !this.userStore?.bannersData?.has_menu_visits;
		},
	},
	methods: {
		saveMenuCustomization() {

			let self = this;
			this.socialsStore.saveSocials(this.$refs['menu-socials'].getSocialCollection(), function (result) {
				if (result === 'OK') {
					self.socialsStore.refreshSocials();

					self.userStore.updateBusiness({
						name: self.userStore.currBusiness.name,
						description: self.userStore.currBusiness.description,
						round_level: self.userStore.currBusiness.round_level,
						banner_size: self.userStore.currBusiness.banner_size,
						color: self.userStore.currBusiness.color,
						logo: self.userStore.currBusiness.logo,
						picture: self.userStore.currBusiness.picture,
					},function (success) {
						if (success) {
							self.showNotification("generic-success");
							self.refreshPreview();
						} else {
							self.showNotification("generic-fail");
						}
					});
				}
			});
		},
		saveMenuFeatures() {
			let self = this;
			this.userStore.updateBusiness({
				enabled_search: self.userStore.currBusiness.enabled_search,
				read_mode: self.userStore.currBusiness.read_mode,
				allergens_url: self.userStore.currBusiness.allergens_url,
				allergens_filter: self.userStore.currBusiness.allergens_filter,
			},function (success) {
				if (success) {
					self.showNotification("generic-success");
					self.refreshPreview();
				} else {
					self.showNotification("generic-fail");
				}
			});
		},
		refreshPreview() {
			this.$refs["menu-preview"].refresh();
		},
	},
	created() {
		if(!this.menuStore.loadedBusinessMenu && this.menuStore.currBusinessId !== null) {
			this.menuStore.loadMenu();
		}

		if (!this.userStore.bannersData?.has_menu_visits) {
			this.userStore.fetchBannersData('has_menu_visits');
		}
	},

	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.menuStore.loadMenu();
				this.userStore.fetchBannersData('has_menu_visits');
			}
		},
	},
}

</script>

<style lang="scss" scoped>
.menu-settings {
	display: flex;
	flex-direction: column;
	gap: 40px;
	:deep(.card-container .card-body) {
		padding: 0;
		& > .list-container {
			.list-item:first-child::before {
				display: none;
			}

			.list-item:last-child::after {
				display: none;
			}

		}
	}

	.extra-text {
		p {
			font-size: 14px;
			color: #9B9B9B;
		}
	}

	.loader-container {
		padding: 20px 0;
	}

	.color-slot .list-item-title {
		width: calc(100% - 45px);
	}

	.image-slot {
		flex-wrap: nowrap;
	}

	.list-item-title-40 {
		max-width: 40%;
		padding-right: 5px;
	}

	.row-clickable {
		cursor: pointer;

		.chevron-container {
			height: 35px;
			display: flex;
			align-items: center;
		}
	}

	.menu-edit-body {
		display: flex;
		flex-direction: row;
		align-items: flex-start;
		position: relative;
		padding: 0;

		.menu-editor-container {
			width: calc(100% - 500px);
			position: relative;

			@media(max-width: 1900px) {
				width: calc(100% - 400px);
			}

			@media(max-width: 1500px) {
				width: calc(100% - 320px);
			}

			@media(max-width: 1200px) {
				width: calc(100% - 230px);
			}
			@media(max-width: 960px) {
				width: calc(100% - 210px);
			}

			@media(max-width: 768px) {
				width: 100%;
				&::after {
					display: none;
				}
			}
		}

		.menu-preview-container {
			width: 500px;
			position: sticky;
			display: flex;
			justify-content: center;
			top: -20px;
			padding: 0 68px 0 80px;
			box-sizing: border-box;
			@media(max-width: 1900px) {
				width: 400px;
				padding: 0 18px 0 30px;
			}

			@media(max-width: 1500px) {
				width: 320px;
				padding: 0 18px 0 30px;
			}

			@media(max-width: 1200px) {
				width: 230px;
				padding: 0 0 0 12px;
			}

			@media(max-width: 960px) {
				width: 210px;

				.container-preview {
					padding: 0;
				}
			}

			@media(max-width: 768px) {
				display: none;
			}

		}
	}

	.card-margin {
		margin-top: var(--main-padding);
	}

	.card-info-banner {
		.card-info-banner-title {
			width: 100%;
			height: fit-content;
			display: flex;
			align-items: flex-start;
			justify-content: flex-start;
			flex-direction: column;
			color: var(--color-txt-white);
			max-width: 400px;
			text-align: left;
			.align-text {
				display:flex;
				flex-direction: column;
			}
			&.divider {
				padding-bottom: 20px;
				border-bottom: 1px solid rgba(255, 255, 255, 0.5);
				width: 100%;
				margin-bottom: 20px;
			}
			h2 {
				font-size: 22px;
				display: inline-block;
				color: var(--color-txt-white);
				@media (max-width: 550px) {
					font-size: 20px;
					line-height: normal;
				}
			}
			p {
				font-size: 16px;
				line-height: 22px;
				color: var(--color-txt-white);
				margin-top: 10px;
				@media (max-width: 550px) {
					font-size: 14px;
					line-height: 20px;
				}
				&.br {
					margin-top: 15px!important;
				}
			}
			.card-info-banner-button {
				margin-top: 20px;
			}
		}
		&:deep(.slashed-card-container) {
			padding: 15px 20px;
			background: linear-gradient(204deg, var(--color-secondary) 10%, rgb(47, 36, 209) 90%);
			height: 100%;
			gap: 40px;
			.slashed-left {
				width: calc(65% - 20px);
				justify-content: flex-start;
				padding: 0;
				overflow: unset;
				border-radius: 0;
				flex-direction: column;
				.card-info-banner-title {
					width: 100%;
					max-width: unset;
				}

				@media (max-width: 550px) {
					width: 100%;
					padding: 0;
					min-height: unset;
					.card-info-banner-title {
						width: 100%;
						max-width: unset;
						padding: 0;
					}
				}
			}
			.slashed-right {
				width: calc(35% - 20px);
				display: flex;
				align-items: center;
				justify-content: center;
				padding: 0;
				@media (max-width: 550px) {
					width: 100%;
					padding: 0;
				}
			}
			@media (max-width: 550px) {
				flex-direction: column-reverse;
				gap: 0;
			}
		}
		.wrapper-img {
			width: 100%;
			height: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
			picture {
				img {
					width: 100%;
					height: 100%;
					max-height: 250px;
					object-fit: contain;
					@media (max-width: 550px) {
						max-height: 180px;
					}
				}
			}
		}
		.icon-link {
			min-height: 40px;
			display: flex;
			align-items: center;
			justify-content: flex-start;
			cursor: pointer;
			gap: 7px;
			margin-top: 10px;
			opacity: .9;
			transition: opacity .2s $transition;
			padding-right: 20px;
			p {
				font-size: 14px;
				line-height: 14px;
				font-weight: 400;
				color: var(--color-secondary);
				margin: 0;
				font-style: italic;
			}
			.wrapper-icon {
				margin-top: 2px;
				width: 12px;
				height: 12px;
				display: flex;
				align-items: center;
				justify-content: center;
				transition: transform .2s $transition;
				transform: translateX(0);
				img {
					width: 100%;
					height: 100%;
					object-fit: contain;
				}
			}
			&:hover {
				opacity: 1;
				.wrapper-icon {
					transform: translateX(2px);
				}
			}
		}
	}

	@media (max-width: 400px) {
		.theme-container p {
			width: 100%;
			margin-bottom: 5px;
		}
		.input-wrapper {
			width: 100%;
		}

		.list-item-title, .list-item-title-40 {
			font-size: 12px;
		}
	}

	@media (max-width: 600px) {
		.text-slot {
			flex-direction: column;

			.list-item-title-half, .list-item-title-40 {
				width: 100%;
				max-width: unset;
			}
		}
		.input-wrapper {
			margin-left: auto;
		}
	}
}
</style>